<template>
  <div class="card">
    <div class="d-flex card-header justify-content-between align-items-center">
      <h4 class="header-title">Assignments</h4>
    </div>

    <div class="card-body pt-0">
      <apexchart height="370" type="line" class="apex-charts pacsort-tooltip-chart mt-3" :series="series" :options="chartOptions"></apexchart>
    </div>
  </div>
</template>

<script lang="ts">
import { buildChartTooltip } from '@/utils/chart-tooltip'
import type { Periods } from '@/typings/Periods'
import { computed, defineComponent, type PropType, ref, watch } from 'vue'
import { differenceInDays } from 'date-fns'
import moment from 'moment/moment'

export default defineComponent({
  props: {
    assignments: {
      type: Object as PropType<Periods>,
      required: true,
    },
  },
  setup(props) {
    const series = ref(getBaseSeries())
    const chartOptions = ref(getBaseOptions())

    function updateSeries() {
      series.value = getBaseSeries()
      chartOptions.value = getBaseOptions()
    }

    function getBaseSeries() {
      return [
        {
          name: 'Selected Period',
          data: props.assignments.currentPeriod,
        },
        {
          name: 'Previous Period',
          data: props.assignments.previousPeriod,
        },
      ]
    }

    const startDate = computed(() => new Date(props.assignments.periodLabels[0]))
    const endDate = computed(() => new Date(props.assignments.periodLabels[props.assignments.periodLabels.length - 1]))
    const shift = computed(() => {
      const daysDiff = Math.round(differenceInDays(endDate.value, startDate.value))
      const moduloShift = 7 - (daysDiff % 7)
      return daysDiff + moduloShift
    })

    function getBaseOptions() {
      return {
        chart: {
          fontFamily: 'PacSort TT Norms, Arial, sans-serif',
          foreColor: '#6c8071',
          animations: { enabled: false },
          dropShadow: {
            enabled: false,
            opacity: 0.2,
            blur: 7,
            left: -7,
            top: 7,
          },
          toolbar: {
            show: false,
          },
          parentHeightOffset: 0,
        },
        grid: {
          strokeDashArray: 7,
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          curve: 'straight',
          dashArray: [0, 5],
          width: 2,
        },
        colors: ['#147c73', '#b4cdbd'],
        zoom: {
          enabled: false,
        },
        legend: {
          show: true,
        },
        tooltip: {
          shared: true,
          intersect: false,
          custom: ({ dataPointIndex }: { dataPointIndex: number }) => {
            const date = moment(props.assignments.periodLabels[dataPointIndex])
            return buildChartTooltip('Assignments', date.format('D MMM YYYY'), '', [
              { name: 'Selected period', value: props.assignments.currentPeriod[dataPointIndex], color: '#147c73' },
              { name: `Previous period (${date.clone().subtract(shift.value, 'days').format('D MMM YYYY')})`, value: props.assignments.previousPeriod[dataPointIndex], color: '#b4cdbd' },
            ], false)
          },
        },
        xaxis: {
          type: 'category',
          tickAmount: 5,
          categories: props.assignments.periodLabels.map((value: string) =>
            moment(value, 'YYYY-MM-DD', true).isValid() ? moment(value).format('D MMM') : value,
          ),
          tooltip: {
            enabled: false,
          },
          axisBorder: {
            show: true,
          },
          labels: { rotate: 0, hideOverlappingLabels: true, formatter: (v: string) => v },
        },
        yaxis: {
          labels: {
            formatter: (val: any) => val,
            offsetX: -15,
          },
        },
      }
    }

    watch(() => props.assignments, updateSeries, { deep: true })

    return {
      series,
      chartOptions,
    }
  },
})
</script>
